import type { MouseEventHandler, ReactNode } from 'react';
import track, { useTracking } from 'react-tracking';

import {
  WarningSolid,
  RewardSolid,
  ArrowRightSolid,
} from '@simplywallst/ui-core/icons';

import { Wrapper, IconWrapper, Button, StyledText } from './styled';

export type Severity = 'minor' | 'major' | 'none';

interface AnchorLinkProps {
  href?: string;
  onClick: MouseEventHandler<HTMLAnchorElement> | undefined;
  children: ReactNode;
}

export const AnchorLink = ({
  children,
  href,
  onClick,
  ...rest
}: AnchorLinkProps) => (
  <Button as="a" href={href} styleType="softLink" onClick={onClick} {...rest}>
    {children}
    <ArrowRightSolid />
  </Button>
);

interface IconProps {
  severity?: Severity;
  size: 'small' | 'default';
}

export const Icon = ({ severity, size }: IconProps) => (
  <IconWrapper severity={severity} size={size}>
    {severity === 'none' || severity === undefined || severity === null ? (
      <RewardSolid />
    ) : (
      <WarningSolid />
    )}
  </IconWrapper>
);

export interface Props {
  name: string;
  index: number;
  severity?: Severity;
  description?: string;
  statementId?: string;
  hash?: string;
  size?: 'small' | 'default';
}

const RiskReward = ({
  name,
  index,
  severity,
  description,
  statementId,
  hash,
  size = 'default',
}: Props) => {
  const { trackEvent } = useTracking();
  return (
    <Wrapper
      severity={severity}
      size={size}
      data-cy-id={`report-risk-${statementId}`}
    >
      <Icon severity={severity} size={size} />
      {description && (
        <StyledText
          typography={size === 'small' ? 'xSmall' : 'small'}
          size={size}
        >
          {typeof hash === 'undefined' ? (
            <>{description}</>
          ) : (
            <AnchorLink
              href={hash}
              onClick={() => {
                trackEvent({
                  modifier: severity ? 'risk' : 'reward',
                  action: 'click',
                  properties: { name, index, severity },
                });
              }}
            >
              {description}
            </AnchorLink>
          )}
        </StyledText>
      )}
    </Wrapper>
  );
};

export default track({ type: 'track', subject: 'risk_reward' })(RiskReward);
